import { Form, Select, SelectProps, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCustomerSearch from '../hooks/customersPage/useCustomerSearch';
import useDebounce from '../hooks/useDebounce';
import { getMaskedEmailForUI } from '../utils/helpers';
import { option } from './CustomFieldComponent';

const { Item } = Form;

interface Props<ValueType> extends Omit<SelectProps<ValueType>, 'options' | 'children' | 'showSearch'> {
    debounceTimeout?: number;
    removeIds?: number[];
}

export default function AutoCompleteCustomerSearchInput({ debounceTimeout = 500, removeIds, ...props }: Props<option>) {
    const { t: tTables } = useTranslation('tables');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const debouncedSearch = useDebounce(searchTerm, debounceTimeout);

    const { data, isLoading, isValidating } = useCustomerSearch(debouncedSearch);
    const customerList = data?.content;

    const options = useMemo(
        () =>
            customerList
                ?.filter(({ id }) => !removeIds.includes(id))
                ?.map(({ id, email, fullName, profilePicture }) => ({
                    value: id,
                    key: id,
                    label: fullName ? `${fullName} (${getMaskedEmailForUI(email)})` : getMaskedEmailForUI(email),
                    profilePicture
                })),
        [customerList, removeIds]
    );

    const isSearchModeActive = searchTerm && searchTerm.length >= 3;
    const validateStatus = isSearchModeActive ? 'success' : searchTerm ? 'error' : 'success';
    const helperText =
        validateStatus === 'error' ? tTables('helperText.moreThan', { count: 3 }) : tTables('helperText.autoComplete');

    return (
        <Item validateStatus={validateStatus} help={helperText}>
            <Select<option>
                labelInValue
                loading={isValidating}
                filterOption={false}
                onSearch={setSearchTerm}
                notFoundContent={isLoading ? <Spin size="small" /> : null}
                placeholder={tTables('placeholder.searchCustomer')}
                {...props}
                options={options}
                showSearch
            />
        </Item>
    );
}
